import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import Select from "react-select";
import Lf23Loader from "../../components/Lf23Loader";
import { API_URL, authHeaders, formatData, ordinaPerStelle, haStella, attivaIl, clicFuori } from "./comuni";
import ListaTrattiOrdinabile from "./ListaTrattiOrdinabile";

/**
 * Convalida di una bozza percorso: assegnatari (obbligatori) + tratte (aggiunta dal picker,
 * rimozione, riordino, stelle). Gemello del modale "Genera percorso operativo", senza il
 * campo data (è quella del giro). Le modifiche alle tratte valgono SOLO per questo giro:
 * l'anagrafica 121 resta invariata.
 * Al "Conferma" chiama PUT /gestione-percorsi/bozze/:id/conferma; la pubblicazione su
 * Interacta la fa il job delle 20:00, non questo modale.
 *
 * Props: bozza (riga di GET /bozze, con `tratti` già risolti), utentiOptions/utentiLoading
 * (lista operatori del padre), onClose, onSaved (dopo un salvataggio ok), top (true se
 * aperto sopra una tabella in fullscreen: alza lo z-index dell'overlay).
 */
export default function ModaleConvalidaBozza({ bozza, utentiOptions, utentiLoading, onClose, onSaved, top = false }) {
    const [operatori, setOperatori] = useState([]);
    const [tratti, setTratti] = useState([]);
    const [recuperoMap, setRecuperoMap] = useState({});
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    // Picker (colonna sinistra): tutti i tratti della 120, caricati all'apertura come nel Genera.
    const [trattiList, setTrattiList] = useState(null);
    const [trattiLoading, setTrattiLoading] = useState(false);
    const [pickerSearch, setPickerSearch] = useState("");

    useEffect(() => {
        let vivo = true;
        setTrattiLoading(true);
        axios.get(`${API_URL}/gestione-percorsi/tratti`, { headers: authHeaders(), params: { page: 1, limit: 100000 } })
            .then((res) => { if (vivo) setTrattiList(res.data?.data ?? []); })
            .catch((err) => { console.error("Errore nel caricamento dei tratti:", err); if (vivo) setTrattiList([]); })
            .finally(() => { if (vivo) setTrattiLoading(false); });
        return () => { vivo = false; };
    }, []);

    const pickerResults = useMemo(() => {
        if (!trattiList) return [];
        const q = pickerSearch.trim().toLowerCase();
        if (!q) return [];
        const selectedIds = new Set(tratti.map((t) => t.id));
        return trattiList
            .filter((t) => !selectedIds.has(t.id))
            // solo le tratte valide per il giorno del giro della bozza (lo verifica anche il backend)
            .filter((t) => attivaIl(t, bozza.data))
            .filter((t) =>
                (t.codice || "").toLowerCase().includes(q) ||
                (t.strada || "").toLowerCase().includes(q) ||
                (t.tratto || "").toLowerCase().includes(q))
            .slice(0, 25);
    }, [trattiList, pickerSearch, tratti, bozza.data]);

    // Tratta aggiunta dal picker: se ha già la stella (automatica) entra in coda al blocco
    // stellato in testa, non in fondo alla lista, così le stelle restano contigue.
    const addTratto = (t) =>
        setTratti((arr) => {
            if (!haStella(t, recuperoMap)) return [...arr, t];
            let to = 0;
            while (to < arr.length && haStella(arr[to], recuperoMap)) to++;
            return [...arr.slice(0, to), t, ...arr.slice(to)];
        });
    const removeTratto = (id) => setTratti((arr) => arr.filter((t) => t.id !== id));

    // Precompila da una bozza già confermata ("Modifica"): assegnatari e stelle salvate.
    // Le stelle automatiche (tratte da recuperare) arrivano dallo stesso endpoint del Genera.
    useEffect(() => {
        let vivo = true;
        const stellate = new Set(bozza.tratte_prioritarie || []);
        const base = (bozza.tratti || []).map((t) => ({ ...t, prioritaria: stellate.has(t.codice) }));
        (async () => {
            let rec = {};
            try {
                const res = await axios.get(`${API_URL}/gestione-percorsi/tratte-recupero`, { headers: authHeaders() });
                rec = res.data?.recupero || {};
            } catch (err) {
                console.error("Priorità di recupero non disponibili:", err);
            }
            if (!vivo) return;
            setRecuperoMap(rec);
            setTratti(ordinaPerStelle(base, rec));
        })();
        return () => { vivo = false; };
    }, [bozza]);

    useEffect(() => {
        const ids = new Set((bozza.assegnatari_ids || []).map(Number));
        setOperatori((utentiOptions || []).filter((o) => ids.has(Number(o.value))));
    }, [bozza, utentiOptions]);

    const conferma = async () => {
        if (!operatori.length) { setError("Seleziona almeno un assegnatario."); return; }
        if (!tratti.length) { setError("Seleziona almeno un tratto."); return; }
        setSaving(true);
        setError("");
        try {
            await axios.put(`${API_URL}/gestione-percorsi/bozze/${bozza.id}/conferma`, {
                assegnatariIds: operatori.map((o) => o.value),
                trattoPostIds: tratti.map((t) => t.id),
                trattePrioritarie: tratti.filter((t) => t.prioritaria).map((t) => t.codice),
            }, { headers: authHeaders() });
            onSaved();
        } catch (err) {
            console.error("Errore conferma bozza:", err);
            // 409 = il job delle 20:00 l'ha già chiusa mentre il modale era aperto: il padre
            // ricarica la lista alla chiusura, qui basta dirlo.
            setError(err.response?.data?.error || "Errore nella conferma. Riprova.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className={`modal-overlay${top ? " gp-overlay-top" : ""}`} {...clicFuori(() => { if (!saving) onClose(); })}>
            <div className="modal wide gp-modal-full gp-modal-fit">
                <div className="modal-header">
                    <div className="m-icon">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>
                    </div>
                    <div style={{ flex: 1 }}>
                        <h3>{bozza.stato === "bozza" ? "Convalida bozza" : "Modifica bozza confermata"}</h3>
                        <div className="h-sub">Scegli gli assegnatari e le tratte del giro: il post su Interacta viene creato automaticamente alle 20:00</div>
                    </div>
                    <button className="modal-close" onClick={onClose} disabled={saving}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                    </button>
                </div>
                {error && <div className="modal-error">{error}</div>}
                <div className="modal-body">
                    <div className="gp-info-section">
                        <h4>{bozza.nome || "—"}</h4>
                        <p className="muted">
                            Itinerario <strong>{bozza.itinerario}</strong> · giro del <strong>{formatData(bozza.data)}</strong> · {tratti.length} tratti
                        </p>
                    </div>

                    <div className="form-group">
                        <label>Assegnatari</label>
                        <Select
                            isMulti
                            classNamePrefix="rs"
                            options={utentiOptions || []}
                            value={operatori}
                            onChange={(vals) => setOperatori(vals || [])}
                            placeholder={utentiLoading ? "Caricamento operatori..." : "Seleziona uno o più operatori"}
                            isLoading={utentiLoading}
                            isDisabled={saving}
                            noOptionsMessage={() => "Nessun operatore"}
                            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                            menuPosition="fixed"
                            styles={{ menuPortal: (base) => ({ ...base, zIndex: 11000 }) }}
                        />
                    </div>

                    {/* Tratti del giro: modifica valida SOLO per questo giro, l'anagrafica 121 resta invariata. */}
                    <div className="section-label">Tratti del giro (modifica solo per questo giro)</div>
                    <div className="gp-tratti-grid">
                        {/* Colonna sinistra: ricerca + risultati (identica al Genera) */}
                        <div className="gp-tratti-col">
                            <div className="picker-add">
                                <input
                                    type="text"
                                    placeholder={trattiLoading ? "Caricamento tratti..." : "Cerca un tratto (codice o via)..."}
                                    value={pickerSearch}
                                    onChange={(e) => setPickerSearch(e.target.value)}
                                    disabled={trattiLoading || saving}
                                />
                                {pickerSearch && (
                                    <button type="button" className="picker-clear" onClick={() => setPickerSearch("")} title="Pulisci ricerca">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                                    </button>
                                )}
                            </div>
                            <div className={`gp-results-box ${pickerSearch ? "has-search" : ""}`}>
                                {trattiLoading ? (
                                    <div className="gp-box-empty">Caricamento tratti...</div>
                                ) : !pickerSearch ? (
                                    <div className="gp-box-empty">Scrivi qui sopra per cercare un tratto per codice o via, poi clicca per aggiungerlo.</div>
                                ) : pickerResults.length === 0 ? (
                                    <div className="gp-box-empty">Nessun tratto trovato.</div>
                                ) : (
                                    pickerResults.map((t) => (
                                        <div key={t.id} className="picker-result" onClick={() => !saving && addTratto(t)}>
                                            <div className="pr-text">
                                                <b>{t.strada || "(senza strada)"}</b>
                                                <span>{t.tratto} · {t.codice}{t.passAnno != null && <span className="gp-pass-badge">{t.passAnno} pass/anno</span>}</span>
                                            </div>
                                            <span className="pr-add">+ aggiungi</span>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                        {/* Colonna destra: tratti selezionati, in ordine */}
                        <div className="gp-tratti-col">
                            <div className="gp-col-head">Selezionati ({tratti.length})</div>
                            <div className="gp-selected-box">
                                <ListaTrattiOrdinabile tratti={tratti} onChange={setTratti} onRemove={removeTratto} recuperoMap={recuperoMap} disabled={saving} />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="modal-footer">
                    <div className="footer-hint">Le bozze non confermate entro le 20:00 vengono scartate.</div>
                    <div className="footer-actions">
                        <button className="btn btn-ghost" onClick={onClose} disabled={saving}>Annulla</button>
                        <button className="btn btn-primary" onClick={conferma} disabled={saving}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12" /></svg>
                            {saving ? "Salvataggio..." : "Conferma"}
                        </button>
                    </div>
                </div>
                {saving && <div className="gp-save-overlay"><Lf23Loader testo="Salvataggio bozza..." /></div>}
            </div>
        </div>
    );
}
